import Image from 'components/Image'
import goodlesImage from 'images/goodles.jpg'
import mccormickImage from 'images/mc-cormick.jpg'
import genVImage1 from 'images/experiential-marketing/genV/image_1.jpg'
import genVImage2 from 'images/experiential-marketing/genV/image_2.jpg'
import genVImage3 from 'images/experiential-marketing/genV/image_3.jpg'
import snickersImage1 from 'images/experiential-marketing/snickers/1.jpg'
import snickersImage2 from 'images/experiential-marketing/snickers/2.jpg'
import snickersImage3 from 'images/experiential-marketing/snickers/3.jpg'
import snickersImage4 from 'images/experiential-marketing/snickers/4.jpeg'
import axploraImage1 from 'images/experiential-marketing/axplora/image_1.jpg'
import axploraImage2 from 'images/experiential-marketing/axplora/image_2.jpg'
import axploraImage3 from 'images/experiential-marketing/axplora/image_3.jpg'
import axploraImage4 from 'images/experiential-marketing/axplora/image_4.jpg'
import grindinNycStreetsImage from 'images/experiential-marketing/grindin/nyc_streets.jpg'
import grindinDailyGrindImage from 'images/experiential-marketing/grindin/daily_grind.jpg'
import grindinHarlemImage from 'images/experiential-marketing/grindin/harlem.jpg'
import grindinIcedCoffeeImage from 'images/experiential-marketing/grindin/iced_coffee.jpg'
import laughingCowBentonvilleImage from 'images/experiential-marketing/laughing-cow/bentonville.jpg'
import laughingCowSamplesImage from 'images/experiential-marketing/laughing-cow/samples.jpg'
import laughingCowPopupImage from 'images/experiential-marketing/laughing-cow/popup.jpg'
import laughingCowMascotImage from 'images/experiential-marketing/laughing-cow/mascot.jpg'
import lfdFullTruckImage from 'images/experiential-marketing/lets-fn-date/full_truck.jpg'
import lfdWithBookImage from 'images/experiential-marketing/lets-fn-date/with_book.jpg'
import lfdServingImage from 'images/experiential-marketing/lets-fn-date/serving.jpg'
import lfdBrandedCupImage from 'images/experiential-marketing/lets-fn-date/branded_cup.jpg'
import LiteVimeo from 'components/LiteVimeo'
import type { StaticImageData } from 'next/legacy/image'
import type { ReactNode } from 'react'

const CaseImage = ({
  alt,
  className,
  sizes,
  src
}: {
  alt: string
  className: string
  sizes: string
  src: StaticImageData
}) => (
  <div
    className={`relative rounded-2xl shadow-xl overflow-hidden ${className}`}
  >
    <Image
      alt={alt}
      layout="fill"
      objectFit="cover"
      placeholder="blur"
      sizes={sizes}
      src={src}
    />
  </div>
)

const Tagline = ({ children }: { children: ReactNode }) => (
  <p className="border-l-4 border-orange-500 pl-4 text-left text-xl sm:text-2xl font-extrabold tracking-tight text-gray-900">
    {children}
  </p>
)

const ExternalLink = ({
  children,
  href
}: {
  children: ReactNode
  href: string
}) => (
  // eslint-disable-next-line @next/next/no-html-link-for-pages
  <a
    className="font-semibold"
    href={href}
    rel="noopener noreferrer"
    target="_blank"
  >
    {children}
  </a>
)

const GrindinSection = () => (
  <div className="py-10 sm:py-16" id="grindin">
    <div className="lg:grid grid-cols-2 gap-x-6 lg:items-start pt-6">
      <div className="space-y-6 text-gray-700 prose lg:prose-lg md:max-w-screen-lg mx-auto text-justify">
        <p className="text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl/none text-left">
          Grindin Coffee by Pharrell Williams + Pusha T
        </p>
        <p>
          When Pharrell Williams and Pusha T launched{' '}
          <strong>Grindin Coffee</strong>, they wanted to introduce the new
          brand in a way that felt just as original as the name behind it. So
          what better way to hit the streets of New York than with a fully
          branded coffee truck?
        </p>
        <p>
          <strong>BFT</strong> helped bring Grindin Coffee to life on the
          streets of NYC with a custom wrapped mobile coffee experience built to
          serve serious volume. Through Grindin’s partnership with legendary
          Italian coffee brand Lavazza, the truck was outfitted with
          professional coffee equipment and staffed with a team of experienced
          baristas ready to keep New Yorkers caffeinated throughout the five day
          tour.
        </p>
        <p>
          From Midtown and SoHo to Brooklyn, the West Village and Harlem, the
          Grindin truck popped up in some of the city’s busiest neighborhoods,
          serving everything on the menu for just <strong>$1.60</strong>. The
          lineup included classics like espresso, cappuccino and lattes
          alongside signature drinks like <strong>P’s Way</strong>, an iced cold
          brew, <strong>T’s Way</strong>, a hot black espresso, and the{' '}
          <strong>Patty Cake</strong>, a caramel latte topped with marshmallow
          foam. Guests could also pick up bags of Grindin’s dark and medium
          roast coffee along with branded merchandise.
        </p>
        <p>
          The $1.60 menu quickly gave New Yorkers a reason to stop, but the
          experience gave them a reason to stick around. The truck became a
          moving introduction to the new brand, putting the product directly
          into people’s hands while creating the kind of organic street level
          buzz that is hard to replicate with traditional advertising.
        </p>
        <p>
          For five days, BFT handled the moving pieces behind the activation,
          helping Grindin Coffee take its launch beyond a traditional pop up and
          turn New York City into its storefront.
        </p>
        <Tagline>
          New brand. Five days. Five iconic NYC neighborhoods. One seriously
          good cup of coffee.
        </Tagline>
      </div>
      <div className="flex flex-col gap-y-6 mt-6 lg:mt-0 lg:sticky lg:top-24">
        <CaseImage
          alt="Grindin Coffee truck on the NYC streets"
          className="aspect-[4/3]"
          sizes="(min-width: 1024px) 600px, 100vw"
          src={grindinNycStreetsImage}
        />
        <CaseImage
          alt="Grindin Coffee $1.60 menu at the truck"
          className="aspect-[4/3]"
          sizes="(min-width: 1024px) 600px, 100vw"
          src={grindinDailyGrindImage}
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4 sm:gap-6 mt-8">
      <CaseImage
        alt="Grindin Coffee truck in Harlem"
        className="aspect-[4/5]"
        sizes="(min-width: 1024px) 600px, 50vw"
        src={grindinHarlemImage}
      />
      <CaseImage
        alt="Grindin iced coffee"
        className="aspect-[4/5]"
        sizes="(min-width: 1024px) 600px, 50vw"
        src={grindinIcedCoffeeImage}
      />
    </div>
  </div>
)

const LaughingCowSection = () => (
  <div className="py-10 sm:py-16" id="laughing-cow">
    <div className="lg:grid grid-cols-2 gap-x-6 lg:items-start pt-6">
      <div className="space-y-6 text-gray-700 prose lg:prose-lg md:max-w-screen-lg mx-auto text-justify">
        <p className="text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl/none text-left">
          The Laughing Cow Wedgemobile Tour
        </p>
        <p>
          When The Laughing Cow took its iconic wedge on the road, BFT helped
          make sure thousands of cheese lovers across the country got a taste.
        </p>
        <p>
          The Wedgemobile tour brought The Laughing Cow to Minneapolis,
          Bentonville and Nashville with playful popups built around sampling,
          brand interaction and, of course, plenty of cheese. Behind the scenes,
          BFT managed the food preparation and catering logistics needed to keep
          each activation running smoothly from city to city.
        </p>
        <p>
          Our team coordinated menus, sourced ingredients and equipment, managed
          local food preparation, and provided the experienced staff needed to
          execute each event. With more than 10,000 samples being prepared and
          served across multiple cities, careful planning was essential. BFT
          also worked through the local health and permitting requirements in
          each market to ensure food was prepared, transported and served safely
          and compliantly.
        </p>
        <p>
          The menu went beyond simply handing out cheese wedges. Guests sampled
          handmade pinwheel bites featuring The Laughing Cow Garlic &amp; Herb
          cheese, along with a zesty elote dip made with The Laughing Cow Light
          cheese. Each recipe required its own ingredients, preparation process,
          equipment and service plan, all coordinated across three different
          markets.
        </p>
        <p>
          The activation is also a great example of what BFT can bring to an
          experiential campaign beyond a traditional food truck. With a
          nationwide network of mobile food vendors, chefs, kitchens and event
          professionals, we can build the food service component around the
          needs of the activation itself. Sometimes that means a fully branded
          mobile kitchen. Other times, it means providing the behind the scenes
          crew to develop menus, source ingredients, secure equipment,
          coordinate food preparation and staff an onsite popup.
        </p>
        <p>
          Big truck or small popup, one city or a multi city tour, BFT connects
          the right people, equipment and food service infrastructure to make it
          happen.
        </p>
        <Tagline>
          Three cities. More than 10,000 samples. One very happy cow. 🐄🧀
        </Tagline>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:gap-6 mt-6 lg:mt-0 lg:sticky lg:top-24">
        <CaseImage
          alt="The Laughing Cow Wedgemobile in Bentonville"
          className="aspect-[4/5]"
          sizes="(min-width: 1024px) 300px, 50vw"
          src={laughingCowBentonvilleImage}
        />
        <CaseImage
          alt="The Laughing Cow pinwheel bites and elote dip samples"
          className="aspect-[4/5]"
          sizes="(min-width: 1024px) 300px, 50vw"
          src={laughingCowSamplesImage}
        />
        <CaseImage
          alt="The Laughing Cow sampling popup"
          className="aspect-[4/5]"
          sizes="(min-width: 1024px) 300px, 50vw"
          src={laughingCowPopupImage}
        />
        <CaseImage
          alt="The Laughing Cow mascot at the popup"
          className="aspect-[4/5]"
          sizes="(min-width: 1024px) 300px, 50vw"
          src={laughingCowMascotImage}
        />
      </div>
    </div>
  </div>
)

const LetsFnDateSection = () => (
  <div className="py-10 sm:py-16" id="lets-fn-date">
    <div className="lg:grid grid-cols-2 gap-x-6 lg:items-start pt-6">
      <div className="space-y-6 text-gray-700 prose lg:prose-lg md:max-w-screen-lg mx-auto text-justify">
        <p className="text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl/none text-left">
          Let’s F#cking Date with Serena Kerrigan
        </p>
        <p>
          When Serena Kerrigan brought <strong>Let’s F#cking Date</strong> from
          screens, card games and sold out live shows to her first book, she
          wanted to celebrate the launch by putting it directly into the hands
          of the community that helped build the brand.
        </p>
        <p>
          BFT helped Serena take over SoHo for a one day New York City pop up
          celebrating the release of{' '}
          <ExternalLink href="https://letsfuckingdate.com/">
            <em>Let’s F#cking Date</em>
          </ExternalLink>
          , her new dating book built around confidence, knowing your worth and
          refusing to settle. Known to her fans as SFK, Serena has built a
          community of more than one million people around her candid and
          hilarious take on modern dating, relationships and self confidence.
        </p>
        <p>
          For the activation,{' '}
          <strong>
            1,000 signed copies of the book were handed out free to fans
          </strong>
          , giving people the chance to meet Serena and get their hands on a
          copy while celebrating the launch right in the heart of SoHo.
        </p>
        <p>
          And because New York decided to serve up an unusually hot fall day,
          BFT brought something else to the party:{' '}
          <strong>free soft serve ice cream</strong>. Served from the truck in
          custom red branded cups with matching spoons, the ice cream became the
          perfect addition to the activation and another fun way to bring the
          bold red <em>Let’s F#cking Date</em> branding to life.
        </p>
        <p>
          Nail polish icon{' '}
          <ExternalLink href="https://www.essie.com/">essie</ExternalLink>{' '}
          joined the activation as a promotional partner, adding another
          recognizable name to the event while keeping Serena, her fans and the
          book at the center of the experience. Essie’s own brand philosophy
          centers heavily around color and self expression, making it a natural
          fit for an activation built around confidence.
        </p>
        <p>
          The result was exactly what a great book launch should feel like:
          personal, fun and a little unexpected. Fans left with a signed copy, a
          sweet treat and a chance to celebrate with the woman behind the brand
          they had already been following for years.
        </p>
        <Tagline>
          1,000 signed books. Free soft serve. One very hot day in SoHo. No
          settling required.
        </Tagline>
      </div>
      <div className="mt-6 lg:mt-0 lg:sticky lg:top-24">
        <CaseImage
          alt="Let’s F#cking Date branded truck in SoHo"
          className="aspect-[2/3]"
          sizes="(min-width: 1024px) 600px, 100vw"
          src={lfdFullTruckImage}
        />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4 sm:gap-6 mt-8">
      <CaseImage
        alt="Serena Kerrigan with her book"
        className="aspect-[2/3]"
        sizes="(min-width: 1024px) 400px, 33vw"
        src={lfdWithBookImage}
      />
      <CaseImage
        alt="Serena Kerrigan serving fans from the truck"
        className="aspect-[2/3]"
        sizes="(min-width: 1024px) 400px, 33vw"
        src={lfdServingImage}
      />
      <CaseImage
        alt="Soft serve in a Let’s F#cking Date branded cup"
        className="aspect-[2/3]"
        sizes="(min-width: 1024px) 400px, 33vw"
        src={lfdBrandedCupImage}
      />
    </div>
  </div>
)

const GenVSection = () => (
  <div
    className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-start py-10 sm:py-16 flex flex-col gap-y-4"
    id="gen-v"
  >
    <div className="relative mx-auto max-w-md sm:max-w-3xl">
      <div>
        <p className="text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl/none">
          Amazon Prime - Gen V
        </p>
        <div className="mt-6 space-y-6 text-gray-700 prose lg:prose-lg md:max-w-screen-lg mx-auto text-justify">
          <p>
            Another amazing branded food truck campaign in the books! BFT was
            happy to work with the{' '}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              className="font-semibold underline-none"
              href="https://www.producexm.com/"
              rel="noopener noreferrer"
              target="_blank"
            >
              Produce Group
            </a>{' '}
            and Amazon Prime to promote their new show{' '}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              className="font-semibold"
              href="https://www.amazon.com/Gen-V-Season-1/dp/B0CBFTRGPZ"
              rel="noopener noreferrer"
              target="_blank"
            >
              &quot;GEN V&quot;
            </a>
            . A spin off of the hit show &quot;The Boys&quot;, the Gen V series
            was targeting the younger college demographic and asked BFT to
            provide full service marketing support through their many channels
            in the mobile industry.
          </p>
          <p>
            BFT did just that by providing a fully custom wrapped food truck to
            mimic a popular fast food chain in the show called &quot;Vought A
            Burger&quot;. Serving up delicious burgers, fries, drinks and merch
            to help engage with college students at high impact moments in Los
            Angeles & New York. BFT spared no details by adding custom food
            packaging, uniforms and a branded bi-coastal delivery team to parade
            the streets of LA & NY, handing out cases of meals to large media
            companies in the film and TV industries.
          </p>
        </div>

        <div className="relative rounded-2xl shadow-xl overflow-hidden flex mt-4">
          <Image
            alt="Gen V image"
            className="object-cover object-center"
            height={800}
            src={genVImage3}
            width={900}
          />
        </div>
      </div>
    </div>

    <div className="flex flex-col gap-y-4 h-full mx-auto max-w-md sm:max-w-3xl">
      <div className="relative rounded-2xl shadow-xl overflow-hidden flex">
        <Image
          alt="Gen V image"
          className="object-cover object-center"
          src={genVImage1}
        />
      </div>
      <div className="relative rounded-2xl shadow-xl overflow-hidden flex">
        <Image
          alt="Gen V image"
          className="object-cover object-center"
          height={1080}
          src={genVImage2}
          width={900}
        />
      </div>
    </div>
  </div>
)
const GoodlesSection = () => (
  <div
    className="lg:grid lg:grid-cols-3 lg:gap-10 lg:items-start pt-6 py-10 sm:py-16"
    id="goodles"
  >
    <div className="col-span-1 relative mx-auto max-w-md sm:max-w-3xl">
      <div className="space-y-6 text-gray-700 prose lg:prose-lg md:max-w-screen-lg mx-auto text-justify">
        <p className="text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl/none">
          GOODLES
        </p>
        <p>
          It was great working with the The GOODLES team to bring out one of our
          best looking experiential marketing branded trucks ever! The multiple
          Mac and Cheese flavors were second to none and everyone was so excited
          to meet celebrity & co-founder of Goodles, Gal Gadot.
        </p>
        <p>
          We love seeing our clients using a food truck to promote their
          products! What a wonderful event in Los Angeles and we look forward to
          the Food Truck Experiential Marketing Events we have planned across
          the country for 2023!
        </p>
        <p>
          Go get yourself a treat and try some GOODLES Mac and Cheese! You
          won&apos;t be disappointed!
        </p>
      </div>
    </div>
    <div className="col-span-1 relative mt-4 mx-auto lg:m-0 max-w-md sm:max-w-3xl">
      <div className="relative aspect-w-9 aspect-h-16">
        <LiteVimeo
          className="rounded-2xl shadow-xl"
          previewId="1577320027-e5b60f99a8ed651051864a2b9a7f0e39d2ba7d950639ce5e8fd69b2a52484fce-d"
          videoId="1023532285"
        />
      </div>
    </div>
    <div className="col-span-1 mx-auto lg:m-0 max-w-md sm:max-w-3xl h-full mt-4 lg:mt-0">
      <div className="rounded-2xl shadow-xl overflow-hidden flex h-full">
        <Image
          alt="GOODLES image"
          className="object-cover object-center aspect-w-9 aspect-h-16 w-full"
          src={goodlesImage}
        />
      </div>
    </div>
  </div>
)
const McCormickSection = () => (
  <div
    className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-start py-10 sm:py-16 flex flex-col gap-y-4"
    id="mccormick"
  >
    <div className="relative mx-auto max-w-md sm:max-w-3xl">
      <div>
        <p className="text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl/none">
          McCormick Taco Seasoning
        </p>
        <div className="mt-6 space-y-6 text-gray-700 prose lg:prose-lg md:max-w-screen-lg mx-auto text-justify">
          <p>
            The household name we’ve always known has hit the streets in style
            with BFT designed activations in Los Angeles, New York and
            Washington DC. Serving hundreds of free tacos, seasoned to
            perfection with the original McCormick Taco Seasoning that’s been in
            our pantries since before we could reach the top shelf. Sometimes we
            need to remind the old generation how great their old favorites are
            while introducing the new generation to the classics by using a
            modern platform they’re familiar with. Original brands like
            McCormick have leveraged a new school trend to deliver an old school
            product to both new and old customers alike.
          </p>
          <p>
            Best Food Trucks (BFT) teamed up with McCormick in 3 major markets,
            providing a one stop shop service to manage everything needed from
            A-Z to make these bi-coastal activations go off without a hitch.
            From renting, designing and wrapping of the food trucks, providing
            experienced chefs, and mixing in some branded swag and supplies, the
            McCormick trucks didn’t go unseen while parked or cruising the
            streets.
          </p>
          <p>
            Creating buzz, educating targeted demographics and increasing
            impressions is what we do best. How can you have the “Best”
            activations without partnering with Best Food Trucks?
          </p>
        </div>
      </div>
    </div>

    <div className="relative mx-auto max-w-md sm:max-w-3xl lg:max-w-none">
      <div className="relative rounded-2xl shadow-xl overflow-hidden flex">
        <Image
          alt="McCormick image"
          className="object-cover object-center"
          height={800}
          src={mccormickImage}
          width={900}
        />
      </div>
    </div>
  </div>
)

const SnickersSection = () => (
  <div className="py-10 sm:py-16" id="snickers">
    <div className="lg:grid grid-cols-2 gap-x-6 pt-6">
      <div className="space-y-6 text-gray-700 prose lg:prose-lg md:max-w-screen-lg mx-auto text-justify">
        <p className="text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl/none text-left">
          Snickers + NFL&apos;s Buffalo Bills + Chef Darian Bryan
        </p>
        <p>
          BFT once again delivered a show-stopping mobile activation, this time
          teaming up with Snickers, Buffalo Bills quarterback Josh Allen, and
          renowned Chef Darian Bryan for an unforgettable game day experience.
        </p>
        <p>
          At the Snickers tailgate party for the Bills’ Sunday season opener,
          fans were introduced to the brand-new line of BBQ Snickers sauces,
          infused with real Snickers ingredients. Guests were treated to bold
          and unexpected flavors like Buffalo Caramel, Peanut Teriyaki, and
          Chocolate BBQ—all brought to life in true Bills Mafia fashion with
          Chef Bryan grilling alongside the hometown hero, Josh Allen. The
          combination of celebrity star power, inventive flavors, and
          high-energy football fans set the stage for a brand experience that
          was both memorable and buzzworthy.
        </p>
        <p>
          As with so many of our experiential activations, BFT elevated the
          event with a stunning, custom-branded BBQ food trailer tailored
          specifically for Snickers. Wrapped in eye-catching visuals that
          highlighted the new sauces, the mobile kitchen served as both a
          culinary hub and a larger-than-life billboard, wowing crowds from
          every angle. By providing Snickers with not just logistics and
          operations but also an engaging, fully branded centerpiece, BFT
          reinforced its reputation as the go-to partner for brands that want to
          make an impact on the road.
        </p>
        <p>
          This activation demonstrated once again how BFT transforms a simple
          tailgate into a powerful marketing moment that connects fans, flavors,
          and brands in a way they’ll never forget.
        </p>
      </div>
      <div>
        <div className="relative rounded-2xl shadow-xl overflow-hidden flex">
          <Image
            alt="Snickers Campaign image"
            className="object-cover object-center"
            height={670}
            src={snickersImage3}
            width={900}
          />
        </div>
        <div className="mt-6 relative rounded-2xl shadow-xl overflow-hidden flex">
          <Image
            alt="Snickers Campaign image"
            className="object-cover object-center"
            height={670}
            src={snickersImage1}
            width={900}
          />
        </div>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4 mt-4">
      <div className="relative rounded-2xl shadow-xl overflow-hidden flex">
        <Image
          alt="Snickers Campaign image"
          className="object-cover object-center"
          height={600}
          src={snickersImage2}
          width={600}
        />
      </div>
      <div className="relative rounded-2xl shadow-xl overflow-hidden flex col-span-2">
        <Image
          alt="Snickers Campaign image"
          className="object-cover object-center"
          height={600}
          src={snickersImage4}
          width={900}
        />
      </div>
    </div>
  </div>
)

const AxploraSection = () => (
  <div className="py-10 sm:py-16" id="axplora">
    <div className="lg:grid grid-cols-2 gap-x-6 pt-6">
      <div className="space-y-6 text-gray-700 prose lg:prose-lg md:max-w-screen-lg mx-auto text-justify">
        <p className="text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl/none text-left">
          Axplora Takes DCAT Week to Another Galaxy 🚀
        </p>
        <p>
          When global pharmaceutical leader Axplora came to New York City for
          DCAT Week, the premier annual gathering of the biopharmaceutical
          business ecosystem, Best Food Trucks helped them find a way to stand
          out in a week packed with high-level meetings and industry
          heavyweights. And they did it by going intergalactic.
        </p>
        <p>
          Over four days in the heart of Manhattan, BFT sourced and fully
          wrapped a local food truck in a custom cosmic design, transforming it
          into the “Axplora Cosmic Dogs” experience. The activation blended bold
          visuals with a playful concept, featuring astronauts serving up New
          York’s iconic Nathan’s hot dogs. The result was a memorable and
          high-energy touchpoint just steps away from DCAT’s busiest corridors.
        </p>
        <p>
          Beyond the spectacle, the activation strategically supported Axplora’s
          presence at DCAT Week, where the company showcases its expertise as a
          global CDMO and API manufacturing partner supporting complex drug
          development and production. By pairing their highly technical offering
          with an approachable and creative street-level experience, Axplora was
          able to spark conversations, attract foot traffic, and leave a lasting
          impression on attendees navigating a fast-paced week of meetings.
        </p>
        <p>
          From concept to execution, BFT handled logistics, permitting, vendor
          coordination, and on-site operations, delivering a seamless multi-day
          activation that cut through the noise and brought real brand
          personality to the streets of NYC.
        </p>
        <p>
          The result? A standout experiential campaign that turned heads, drove
          engagement, and proved that even in one of the most buttoned-up
          industries, creativity still wins.
        </p>
      </div>
      <div>
        <div className="relative rounded-2xl shadow-xl overflow-hidden flex">
          <Image
            alt="Axplora Cosmic Dogs image"
            className="object-cover object-center"
            height={768}
            src={axploraImage2}
            width={1024}
          />
        </div>
        <div className="mt-6 relative rounded-2xl shadow-xl overflow-hidden flex">
          <Image
            alt="Axplora Cosmic Dogs image"
            className="object-cover object-center"
            height={768}
            src={axploraImage4}
            width={1024}
          />
        </div>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4 mt-4">
      <div className="relative rounded-2xl shadow-xl overflow-hidden flex">
        <Image
          alt="Axplora Cosmic Dogs image"
          className="object-cover object-center"
          height={768}
          src={axploraImage3}
          width={1024}
        />
      </div>
      <div className="relative rounded-2xl shadow-xl overflow-hidden flex">
        <Image
          alt="Axplora Cosmic Dogs image"
          className="object-cover object-center"
          height={875}
          src={axploraImage1}
          width={1024}
        />
      </div>
    </div>
  </div>
)

const Tabs = () => (
  <div className="flex flex-wrap mt-4 gap-x-4 gap-y-2">
    {[
      {
        name: 'Grindin Coffee - Pharrell Williams + Pusha T',
        link: 'grindin'
      },
      {
        name: 'The Laughing Cow - Wedgemobile Tour',
        link: 'laughing-cow'
      },
      {
        name: 'Let’s F#cking Date - Serena Kerrigan',
        link: 'lets-fn-date'
      },
      {
        name: 'Axplora - DCAT Week',
        link: 'axplora'
      },
      {
        name: "Snickers + NFL's Buffalo Bills + Chef Darian Bryan",
        link: 'snickers'
      },
      {
        name: 'Amazon Prime - Gen V',
        link: 'gen-v'
      },
      {
        name: 'GOODLES',
        link: 'goodles'
      },
      {
        name: 'McCormick Taco Seasoning',
        link: 'mccormick'
      }
    ].map((tab) => (
      // eslint-disable-next-line @next/next/no-html-link-for-pages
      <a
        key={tab.name}
        className="text-gray-700 hover:text-gray-800 font-light border-solid border border-gray-300 rounded-md px-2 py-1 text-sm cursor-pointer"
        href={`#${tab.link}`}
      >
        {tab.name}
      </a>
    ))}
  </div>
)

const ClientCaseSection = () => (
  <div className="w-full">
    <div className="lg:mx-auto lg:max-w-7xl px-4 pb-12 md:pb-24 pt-8 md:pt-12">
      <div className="relative max-w-md sm:max-w-3xl sm:mx-auto lg:mx-0">
        <h2 className="text-2xl font-semibold text-orange-600 uppercase tracking-wide">
          Recent Satisfied Clients
        </h2>
      </div>
      <Tabs />
      <div className="flex flex-col gap-y-10 lg:gap-y-20 divide-solid divide-gray-200 divide-y">
        <GrindinSection />
        <LaughingCowSection />
        <LetsFnDateSection />
        <AxploraSection />
        <SnickersSection />
        <GenVSection />
        <GoodlesSection />
        <McCormickSection />
      </div>
    </div>
  </div>
)

export default ClientCaseSection
